import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock, LogOut } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useSessionTimeout } from "../hooks/useSessionTimeout";

// Defaults: 30 min idle, 60 sec warning. Matches medical-grade app expectations.
const IDLE_MS = 30 * 60 * 1000;
const WARNING_MS = 60 * 1000;

/**
 * SessionTimeoutManager
 *
 * Renders a warning dialog when the authenticated user is idle.
 * Forces logout (clear token + redirect home) when countdown reaches 0.
 *
 * Should be mounted ONLY when an authenticated user exists.
 */
const SessionTimeoutManager = ({ enabled, onLogout }) => {
  const navigate = useNavigate();

  const performLogout = useCallback((reason) => {
    try {
      localStorage.removeItem("auth_token");
    } catch {
      // ignore
    }
    if (onLogout) onLogout();
    navigate("/auth");
    if (reason === "timeout") {
      toast.info("Sesión cerrada por inactividad", {
        description: "Por seguridad, te desconectamos automáticamente.",
        duration: 6000,
      });
    } else {
      toast.success("Sesión cerrada", { duration: 3000 });
    }
  }, [navigate, onLogout]);

  const handleTimeout = useCallback(() => performLogout("timeout"), [performLogout]);
  const handleManualLogout = useCallback(() => performLogout("manual"), [performLogout]);

  const { warning, secondsLeft, stayActive } = useSessionTimeout({
    enabled,
    idleMs: IDLE_MS,
    warningMs: WARNING_MS,
    onTimeout: handleTimeout,
  });

  if (!enabled) return null;

  return (
    <Dialog open={warning} onOpenChange={(open) => { if (!open) stayActive(); }}>
      <DialogContent className="sm:max-w-md" data-testid="session-timeout-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-600" />
            Tu sesión va a expirar
          </DialogTitle>
          <DialogDescription>
            Detectamos inactividad. Por seguridad, vamos a cerrar tu sesión en{" "}
            <strong className="text-amber-700" data-testid="session-timeout-seconds">
              {secondsLeft}
            </strong>{" "}
            {secondsLeft === 1 ? "segundo" : "segundos"}.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900">
          ¿Querés continuar trabajando? Hacé click en <strong>Seguir conectado</strong>.
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={handleManualLogout}
            data-testid="session-timeout-logout-btn"
            className="text-slate-700"
          >
            <LogOut className="h-4 w-4 mr-1" />
            Cerrar sesión
          </Button>
          <Button
            onClick={stayActive}
            data-testid="session-timeout-stay-btn"
            className="bg-[#1d366b] hover:bg-[#152a52] text-white"
          >
            Seguir conectado
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SessionTimeoutManager;
